import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import { 
  Calendar, 
  Clock, 
  Phone, 
  Castle,
  Globe,
  ArrowLeft,
  Inbox,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnlinePendingPage() {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      const response = await fetch(`${API}/reservations/online-pending`, { 
        credentials: "include" 
      });
      if (!response.ok) throw new Error("Failed to fetch");
      setReservations(await response.json());
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return format(new Date(dateStr), "EEEE d MMMM yyyy", { locale: fr });
  };

  const formatDateTime = (dateStr) => {
    return format(new Date(dateStr), "d MMM à HH:mm", { locale: fr });
  };

  const timeSlotNames = {
    "matin": "Matin (8h-13h)",
    "apres-midi": "Après-midi (14h-19h)",
    "journee": "Journée complète",
  };

  if (loading) {
    return (
      <div className="min-h-screen festive-gradient flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-festive-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen festive-gradient confetti-pattern page-container">
      <Header title="Demandes en ligne" showBack />
      
      <main className="p-4 space-y-4 max-w-2xl mx-auto">
        {/* Header Card */}
        <Card className="card-festive bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
                <Globe className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900">Nouvelles demandes</h2>
                <p className="text-sm text-gray-600">
                  {reservations.length} demande{reservations.length > 1 ? "s" : ""} en attente de traitement
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reservations List */}
        {reservations.length === 0 ? (
          <Card className="card-festive">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-50 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Tout est traité !</h3>
              <p className="text-gray-500 mb-4">
                Aucune nouvelle demande en ligne en attente.
              </p>
              <Button
                onClick={() => navigate("/calendar")}
                variant="outline"
                className="rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au planning
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reservations.map((reservation) => {
              const isUnviewed = !reservation.viewed_at;
              
              return (
                <Card 
                  key={reservation.id}
                  className={`card-festive cursor-pointer transition-all hover:shadow-lg ${
                    isUnviewed ? "ring-2 ring-cyan-300 ring-offset-2" : ""
                  }`}
                  onClick={() => navigate(`/reservations/${reservation.id}`)}
                  data-testid={`online-reservation-${reservation.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`relative w-12 h-12 rounded-xl flex items-center justify-center ${
                          isUnviewed 
                            ? "bg-gradient-to-br from-cyan-100 to-blue-100" 
                            : "bg-gray-100"
                        }`}>
                          <Castle className={`w-6 h-6 ${isUnviewed ? "text-cyan-600" : "text-gray-500"}`} />
                          {isUnviewed && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {reservation.client_name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Reçu le {formatDateTime(reservation.created_at)}
                          </p>
                        </div>
                      </div>
                      <StatusBadge status={reservation.status} showNotification={isUnviewed} />
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4 text-cyan-500" />
                        <span className="font-medium">{formatDate(reservation.date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="w-4 h-4 text-cyan-500" />
                        <span>{timeSlotNames[reservation.time_slot] || reservation.time_slot}</span>
                      </div>
                      <a
                        href={`tel:${reservation.client_phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 text-cyan-600 hover:underline"
                      >
                        <Phone className="w-4 h-4" />
                        <span>{reservation.client_phone}</span>
                      </a>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        Total: <span className="font-bold text-cyan-600">{reservation.total_price}€</span>
                      </span>
                      <Button
                        size="sm"
                        className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/reservations/${reservation.id}`);
                        }}
                      >
                        Traiter
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
